import { safeFormatMessage } from '@10xlabs/utils'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { BusOutlinedIcon } from 'components/Icons/BusOutlinedIcon'
import { PassengerWithSeatIcon } from 'components/Icons/PassengerWithSeatIcon'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { StyledLink } from 'components/v2/Link'
import { IsAssigned } from 'components/Chip'
import { vehicleTypeMessages } from 'features/Vehicles/messages'
import { Dropdown } from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { useLogin } from 'features/Login/hooks'
import { canWrite } from 'features/Users/utils'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { VehicleActions } from 'features/Vehicles/components/VehiclesList/Actions'
import { VehicleStatus } from 'features/Vehicles/components/Status'
import { GTTVehicle } from 'gql/types.generated'

interface IVehiclesV2ListProps {
  vehicles: GTTVehicle[]
}

const TemplateWraper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const TemplateTypeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TemplateCapacityWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledBusOutlinedIcon = styled(BusOutlinedIcon)`
  fill: #916ee8;
  height: 20px;
  width: 20px;
`

const PartnerLabel = styled.span`
  color: #717171;
`

const VehiclesV2List = ({ vehicles }: IVehiclesV2ListProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const canSeeActions = canWrite(FEATURES.VEHICLE, login.accessToken.policies)

  return (
    <div>
      <TableContainer overflow>
        <Table>
          <Thead>
            <Tr>
              {canSeeActions && <Th />}
              <Th>
                <FormattedMessage id="vehiclesV2.list.id" defaultMessage="Vehicle ID" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.status" defaultMessage="Status" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.offerStatus" defaultMessage="Offer status" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.createdAt" defaultMessage="Date of creation" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.name" defaultMessage="Name" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.type" defaultMessage="Type" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.make" defaultMessage="Make" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.model" defaultMessage="Model" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.plates" defaultMessage="Plates" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.drivers" defaultMessage="Drivers" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.partner" defaultMessage="Partner" />
              </Th>
              <Th>
                <FormattedMessage id="vehiclesV2.list.services" defaultMessage="Services" />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {vehicles.map((vehicle) => {
              const plates = vehicle.licensePlates ?? []

              return (
                <Tr key={vehicle.id}>
                  {canSeeActions && (
                    <Td>
                      <VehicleActions vehicle={vehicle} />
                    </Td>
                  )}
                  <Td>{vehicle.id.substring(0, 8)}</Td>
                  <Td>
                    <VehicleStatus status={vehicle.status} />
                  </Td>
                  <Td>
                    <IsAssigned isAssigned={vehicle.isAssigned} />
                  </Td>
                  <Td>
                    <FormattedDate intl={intl} value={vehicle.createdAt} month="2-digit" day="2-digit" year="numeric" />
                  </Td>
                  <Td>
                    {vehicle.name} {vehicle.externalID ? `(${vehicle.externalID})` : null}
                  </Td>
                  <Td>
                    <TemplateWraper>
                      <TemplateTypeWrapper>
                        <StyledBusOutlinedIcon />
                        {safeFormatMessage({ intl, messages: vehicleTypeMessages, key: vehicle.template?.type })}
                      </TemplateTypeWrapper>
                      <TemplateCapacityWrapper>
                        <PassengerWithSeatIcon />
                        {vehicle.template?.capacity}
                      </TemplateCapacityWrapper>
                    </TemplateWraper>
                  </Td>
                  <Td>{vehicle.features?.make ?? '-'}</Td>
                  <Td>{vehicle.features?.model ?? '-'}</Td>
                  <Td>
                    {isEmpty(plates) && '-'}
                    {plates.length === 1 && plates[0].number}
                    {plates.length > 1 && (
                      <Dropdown toggle={<StyledLink>{plates.length}</StyledLink>} noPadding>
                        <List.Container>
                          {plates.map(({ number }): ReactElement => {
                            return <List.Item key={number}>{number}</List.Item>
                          })}
                        </List.Container>
                      </Dropdown>
                    )}
                  </Td>
                  <Td>
                    {isEmpty(vehicle.drivers) && 0}
                    {vehicle.drivers.length === 1 && <StyledLink>{vehicle.drivers[0].name}</StyledLink>}
                    {vehicle.drivers.length > 1 && (
                      <Dropdown toggle={<StyledLink>{vehicle.drivers.length}</StyledLink>} noPadding>
                        <List.Container>
                          {vehicle.drivers.map(({ id, name }): ReactElement => {
                            return <List.Item key={id}>{name}</List.Item>
                          })}
                        </List.Container>
                      </Dropdown>
                    )}
                  </Td>
                  <Td>
                    <PartnerLabel>{vehicle.partner?.name}</PartnerLabel>
                  </Td>
                  <Td>
                    {isEmpty(vehicle.routeServices) && '-'}
                    {vehicle.routeServices.length > 0 && (
                      <Dropdown toggle={<StyledLink>{vehicle.routeServices.length}</StyledLink>} noPadding>
                        <List.Container>
                          {vehicle.routeServices.map(({ id, code }): ReactElement => {
                            return <List.Item key={id}>{code}</List.Item>
                          })}
                        </List.Container>
                      </Dropdown>
                    )}
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default VehiclesV2List
